import React from "react";
import { interpolOperations } from "./data/interpolOperations";

const OperasiInterpol: React.FC = () => {
  const getImageDescription = (
    operationName: string,
    imageIndex: number,
  ): string => {
    const operationDescriptions: Record<string, string[]> = {
      "OPERASI SEAF (SOUTHEAST AND EAST ASIA FUGITIVES)": [
        "Tim koordinasi SEAF",
        "Sistem pelacakan buronan",
        "Kerjasama bilateral negara SEAF",
        "Analisis pergerakan tersangka",
        "Identifikasi lokasi subyek",
      ],
      "OPERASI LIONFISH": [
        "Jaringan distribusi narkoba",
        "Platform intelijen I-RAID",
        "Operasi pencegahan narkotika",
        "Kerjasama internasional anti-narkoba",
        "Sistem deteksi prekursor kimia",
      ],
      "OPERASI SECURE": [
        "Infrastruktur malware",
        "Server kriminal yang diamankan",
        "Data teknis cybercrime",
        "Operasi ASPJOC",
      ],
      "OPERASI JACKAL": [
        "Jaringan keuangan Afrika Barat",
        "Infrastruktur Black Axe",
      ],
      "OPERASI LIBERTERRA": [
        "Operasi anti-trafficking",
        "Jaringan perdagangan manusia",
        "Penyelundupan migran",
        "Kerjasama global anti-trafficking",
      ],
      "OPERASI STORMMAKERS": [
        "Penipuan siber trafficking",
        "Eksploitasi pekerja migran",
      ],
    };

    const descriptions = operationDescriptions[operationName];
    if (descriptions && descriptions[imageIndex]) {
      return descriptions[imageIndex];
    }

    return `aktivitas operasi ${imageIndex + 1}`;
  };

  return (
    <div>
      <div className="mx-auto w-11/12 laptop:max-w-full desktop:max-w-7xl">
        <div className="flex flex-row my-12">
          <div className="p-4 w-full">
            <div className="card w-full bg-base-100 shadow-xl border-t-8 border-blue-600 rounded-xl">
              <div className="card-body p-0">
                <div className="flex flex-row px-6 pt-6">
                  <div className="grow">
                    <h2 className="card-title text-3xl font-bold text-base-content">
                      Operasi INTERPOL
                    </h2>
                    <p className="text-base-content/70 mt-2">
                      Operasi-operasi Global INTERPOL dalam Pemberantasan
                      Kejahatan Lintas Negara
                    </p>
                  </div>
                  <div className="shrink">
                    <div className="text-sm breadcrumbs">
                      <ul>
                        <li>
                          <a
                            href="/"
                            className="text-base-content/60 hover:text-base-content"
                          >
                            Beranda
                          </a>
                        </li>
                        <li className="text-base-content/60">NCB INTERPOL</li>
                        <li className="text-primary font-semibold">
                          Operasi INTERPOL
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="divider m-0"></div>

                <div className="p-6">
                  {/* Introduction */}
                  <div className="bg-base-200 rounded-xl p-6 mb-8">
                    <h3 className="text-xl font-bold text-base-content mb-4">
                      Tentang Operasi INTERPOL
                    </h3>
                    <p className="text-base-content/70 leading-relaxed">
                      INTERPOL menyelenggarakan berbagai operasi global untuk
                      memberantas kejahatan lintas negara dengan melibatkan
                      kerja sama antarnegara. Indonesia melalui NCB INTERPOL
                      aktif berpartisipasi dalam operasi-operasi strategis yang
                      mencakup pemberantasan terorisme, kejahatan siber,
                      perdagangan manusia, narkotika, dan berbagai bentuk
                      kejahatan transnasional lainnya.
                    </p>
                  </div>

                  {/* Operations */}
                  <div className="space-y-8">
                    {interpolOperations.map((operation, index) => (
                      <div key={index} className="bg-base-200 rounded-xl p-6">
                        <div className="flex flex-col lg:flex-row gap-6">
                          {/* Content Section */}
                          <div
                            className={`${operation.images ? "lg:w-2/3" : "w-full"}`}
                          >
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-4">
                              <h3 className="text-xl font-bold text-primary">
                                {operation.name}
                              </h3>
                              {operation.period && (
                                <span className="badge badge-secondary badge-lg">
                                  {operation.period}
                                </span>
                              )}
                            </div>

                            <p className="text-base-content/70 leading-relaxed mb-4">
                              {operation.description}
                            </p>

                            {operation.additionalInfo && (
                              <div className="bg-base-100 rounded-lg p-4 mb-4">
                                <h4 className="font-semibold text-base-content mb-2">
                                  {operation.additionalInfo}
                                </h4>
                                {operation.driveLink && (
                                  <a
                                    href={operation.driveLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary hover:text-primary-focus underline text-sm"
                                  >
                                    Lihat Dokumentasi →
                                  </a>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Images Section */}
                          {operation.images && operation.images.length > 0 && (
                            <div className="lg:w-1/3">
                              <div className="grid gap-3">
                                {operation.images.map((image, imgIndex) => (
                                  <div
                                    key={imgIndex}
                                    className="bg-base-100 rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow"
                                  >
                                    <img
                                      src={image}
                                      alt={`${operation.name} - ${getImageDescription(operation.name, imgIndex)}`}
                                      loading="lazy"
                                      className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300 cursor-pointer"
                                      onError={(e) => {
                                        const target =
                                          e.target as HTMLImageElement;
                                        target.src = "/placeholder-image.jpg";
                                        target.alt = "Gambar tidak tersedia";
                                      }}
                                      onClick={() =>
                                        window.open(image, "_blank")
                                      }
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Summary Statistics */}
                  <div className="bg-gradient-to-r from-primary/20 to-secondary/20 rounded-xl p-6 mt-8">
                    <h3 className="text-xl font-bold text-base-content mb-4">
                      Ringkasan Operasi
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center bg-base-100 rounded-lg p-4">
                        <div className="text-2xl font-bold text-primary">
                          10
                        </div>
                        <div className="text-sm text-base-content/70">
                          Total Operasi
                        </div>
                      </div>
                      <div className="text-center bg-base-100 rounded-lg p-4">
                        <div className="text-2xl font-bold text-secondary">
                          138
                        </div>
                        <div className="text-sm text-base-content/70">
                          Negara Terlibat
                        </div>
                      </div>
                      <div className="text-center bg-base-100 rounded-lg p-4">
                        <div className="text-2xl font-bold text-success">
                          22
                        </div>
                        <div className="text-sm text-base-content/70">
                          Dokumentasi Visual
                        </div>
                      </div>
                      <div className="text-center bg-base-100 rounded-lg p-4">
                        <div className="text-2xl font-bold text-accent">
                          15+
                        </div>
                        <div className="text-sm text-base-content/70">
                          Tahun Pengalaman
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Key Focus Areas */}
                  <div className="bg-base-200 rounded-xl p-6 mt-8">
                    <h3 className="text-xl font-bold text-base-content mb-6">
                      Area Fokus Utama
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="bg-base-100 rounded-lg p-4 text-center">
                        <div className="text-3xl mb-2">💻</div>
                        <h4 className="font-semibold text-base-content mb-2">
                          Kejahatan Siber
                        </h4>
                        <p className="text-sm text-base-content/70">
                          Penipuan finansial, malware, dan kejahatan digital
                        </p>
                      </div>
                      <div className="bg-base-100 rounded-lg p-4 text-center">
                        <div className="text-3xl mb-2">💊</div>
                        <h4 className="font-semibold text-base-content mb-2">
                          Obat Ilegal
                        </h4>
                        <p className="text-sm text-base-content/70">
                          Pemberantasan farmasi palsu dan obat berbahaya
                        </p>
                      </div>
                      <div className="bg-base-100 rounded-lg p-4 text-center">
                        <div className="text-3xl mb-2">🌿</div>
                        <h4 className="font-semibold text-base-content mb-2">
                          Narkotika
                        </h4>
                        <p className="text-sm text-base-content/70">
                          Penindakan jalur distribusi narkoba internasional
                        </p>
                      </div>
                      <div className="bg-base-100 rounded-lg p-4 text-center">
                        <div className="text-3xl mb-2">👥</div>
                        <h4 className="font-semibold text-base-content mb-2">
                          Perdagangan Manusia
                        </h4>
                        <p className="text-sm text-base-content/70">
                          Pemberantasan trafficking dan eksploitasi
                        </p>
                      </div>
                      <div className="bg-base-100 rounded-lg p-4 text-center">
                        <div className="text-3xl mb-2">🔒</div>
                        <h4 className="font-semibold text-base-content mb-2">
                          Terorisme
                        </h4>
                        <p className="text-sm text-base-content/70">
                          Counter-terrorism di Asia Tenggara
                        </p>
                      </div>
                      <div className="bg-base-100 rounded-lg p-4 text-center">
                        <div className="text-3xl mb-2">🐾</div>
                        <h4 className="font-semibold text-base-content mb-2">
                          Perdagangan Satwa
                        </h4>
                        <p className="text-sm text-base-content/70">
                          Perlindungan flora dan fauna dilindungi
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperasiInterpol;
